// ManufacturerDataHealthOverview.tsx
//
// Cross-manufacturer audit table. One row per manufacturer with manual vs derived
// brand counts, suggested isGeneric, current-vs-suggested primary, and the diff
// (brands new-in-derived / stale-in-manual / excluded). Sortable — defaults to
// biggest diffs first (most actionable) — and filterable to "has diffs" only.
// Clicking a chip label drills into the contributing-refs modal; clicking the row
// opens that manufacturer's detail.
//
// Phase 3c adds the same inline WRITE actions the per-manufacturer Data Health
// section already has (PR #284): promote / exclude a new-in-derived brand, demote
// a stale one (confirm-gated), restore an excluded one, and set the suggested
// primary — each an immediate single-field write via the shared firebase.ts
// helpers, with optimistic UI. Edits bubble up via `onManufacturerChange` so the
// parent list / open edit modal stay in sync. Action buttons reveal on row hover
// (or keyboard focus) to keep the audit table scannable at rest.

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { ManufacturerCatalogueEntry } from '../../services/firebase';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import {
  demoteManufacturerBrand,
  excludeManufacturerBrand,
  promoteManufacturerBrand,
  setManufacturerPrimaryBrand,
  unexcludeManufacturerBrand,
} from '../../services/firebase';
import {
  BrandChip,
  brandLabel,
  ChipButton,
  ChipLegend,
  useWatchBrandsMap,
} from './dataHealthShared';
import { ManufacturerBrandDrillDown } from './ManufacturerBrandDrillDown';
import {
  computeManufacturerDataHealth,
  type ManufacturerDataHealth,
} from './manufacturerDataHealth';

interface Row {
  manufacturer: ManufacturerCatalogueEntry;
  name: string;
  health: ManufacturerDataHealth;
}

type SortKey = 'diff' | 'name' | 'manual' | 'derived';

const CHIP_CAP = 5;

/** Buttons are in-flow only on row hover / focus, so the table reads clean at rest. */
const REVEAL = 'hidden group-hover:inline-flex group-focus-within:inline-flex';

/** minus/plus a value from a string[] (sorted-unique add, filtered remove). */
function addTo(arr: string[] | undefined, v: string): string[] {
  return Array.from(new Set([...(arr ?? []), v])).sort();
}
function removeFrom(arr: string[] | undefined, v: string): string[] {
  return (arr ?? []).filter((x) => x !== v);
}

/** The inline quick-action callbacks + shared busy flag, threaded to the cells. */
interface RowActions {
  busy: boolean;
  promote: (m: ManufacturerCatalogueEntry, brandId: string) => void;
  exclude: (m: ManufacturerCatalogueEntry, brandId: string) => void;
  unexclude: (m: ManufacturerCatalogueEntry, brandId: string) => void;
  confirmDemote: (m: ManufacturerCatalogueEntry, brandId: string) => void;
  setPrimary: (m: ManufacturerCatalogueEntry, brandId: string) => void;
  clearPrimary: (m: ManufacturerCatalogueEntry) => void;
}

interface ConfirmState {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
}

function classificationBadge(h: ManufacturerDataHealth) {
  return h.suggestedIsGeneric ? (
    <Badge variant="default">Generic</Badge>
  ) : h.suggestedClassification === 'semi-generic' ? (
    <Badge variant="info">Semi-generic</Badge>
  ) : (
    <Badge variant="success">Non-generic</Badge>
  );
}

export function ManufacturerDataHealthOverview({
  manufacturers,
  onOpenManufacturer,
  onManufacturerChange,
}: {
  manufacturers: ManufacturerCatalogueEntry[];
  onOpenManufacturer: (m: ManufacturerCatalogueEntry) => void;
  /** Bubbles optimistic (and reverted) inline edits up so the list / edit modal stay fresh. */
  onManufacturerChange?: (updated: ManufacturerCatalogueEntry) => void;
}) {
  const { brandsById, loading } = useWatchBrandsMap();
  const [diffsOnly, setDiffsOnly] = useState(false);
  const [hideGeneric, setHideGeneric] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('diff');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Which (manufacturer, brand) pair the contributing-refs drill-down is open for.
  const [drill, setDrill] = useState<{
    manufacturer: ManufacturerCatalogueEntry;
    brandId: string;
  } | null>(
    null,
  );

  // Optimistic local overlay — resynced whenever the prop changes (the parent
  // patches the same manufacturer on bubble-up, so this is a no-op after our own
  // edits and picks up external refreshes otherwise).
  const [localManufacturers, setLocalManufacturers] = useState(manufacturers);
  useEffect(() => setLocalManufacturers(manufacturers), [manufacturers]);

  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const label = useCallback((id: string) => brandLabel(brandsById, id), [brandsById]);

  // Optimistically swap the manufacturer in-place, persist, revert both the local
  // overlay and the bubbled-up copy on failure.
  const run = useCallback(
    async (
      next: ManufacturerCatalogueEntry,
      persist: () => Promise<void>,
      successMsg: string,
    ) => {
      let prev: ManufacturerCatalogueEntry | undefined;
      setLocalManufacturers((list) =>
        list.map((m) => {
          if (m.id !== next.id) return m;
          prev = m;
          return next;
        }),
      );
      onManufacturerChange?.(next);
      setBusy(true);
      try {
        await persist();
        toast.success(successMsg);
      } catch (err) {
        console.error('[DataHealth overview] quick action failed', err);
        toast.error(err instanceof Error ? err.message : 'Update failed');
        if (prev) {
          const reverted = prev;
          setLocalManufacturers((list) => list.map((m) => (m.id === reverted.id ? reverted : m)));
          onManufacturerChange?.(reverted);
        }
      } finally {
        setBusy(false);
      }
    },
    [onManufacturerChange],
  );

  const actions = useMemo<RowActions>(
    () => ({
      busy,
      promote: (m, brandId) =>
        void run(
          { ...m, brandIdsManualInclude: addTo(m.brandIdsManualInclude, brandId) },
          () => promoteManufacturerBrand(m.id, brandId),
          `Promoted ${label(brandId)} to manual list`,
        ),
      exclude: (m, brandId) =>
        void run(
          { ...m, brandExclusions: addTo(m.brandExclusions, brandId) },
          () => excludeManufacturerBrand(m.id, brandId),
          `Excluded ${label(brandId)}`,
        ),
      unexclude: (m, brandId) =>
        void run(
          { ...m, brandExclusions: removeFrom(m.brandExclusions, brandId) },
          () => unexcludeManufacturerBrand(m.id, brandId),
          `Restored ${label(brandId)}`,
        ),
      setPrimary: (m, brandId) =>
        void run(
          { ...m, primaryBrandId: brandId },
          () => setManufacturerPrimaryBrand(m.id, brandId),
          `Set primary brand to ${label(brandId)}`,
        ),
      // Clear the primary brand. `setManufacturerPrimaryBrand(id, '')`
      // deleteField()s it; optimistically '' reads as unset in the health compute.
      clearPrimary: (m) =>
        void run(
          { ...m, primaryBrandId: '' },
          () => setManufacturerPrimaryBrand(m.id, ''),
          'Cleared primary brand',
        ),
      confirmDemote: (m, brandId) =>
        setConfirm({
          title: 'Remove manual brand link',
          body: `Remove ${label(brandId)} from ${
            m.displayName || m.name || m.id
          }'s manual list? Refs using this manufacturer no longer count this brand as manually-linked.`,
          confirmLabel: 'Remove',
          onConfirm: () => {
            setConfirm(null);
            void run(
              { ...m, brandIdsManualInclude: removeFrom(m.brandIdsManualInclude, brandId) },
              () => demoteManufacturerBrand(m.id, brandId),
              `Removed ${label(brandId)} from manual list`,
            );
          },
        }),
    }),
    [busy, run, label],
  );

  const rows = useMemo<Row[]>(
    () =>
      localManufacturers.map((m) => ({
        manufacturer: m,
        name: m.displayName || m.name || m.id,
        health: computeManufacturerDataHealth(m),
      })),
    [localManufacturers],
  );

  const withDiffsCount = useMemo(
    () => rows.filter((r) => r.health.diffCount > 0).length,
    [rows],
  );

  const visible = useMemo(() => {
    // Filters are additive: "has diffs" and "hide generic" both narrow the set.
    const filtered = rows.filter(
      (r) =>
        (!diffsOnly || r.health.diffCount > 0) &&
        (!hideGeneric || !r.health.suggestedIsGeneric),
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'manual':
          cmp = a.health.manual.length - b.health.manual.length;
          break;
        case 'derived':
          cmp = a.health.derived.length - b.health.derived.length;
          break;
        case 'diff':
        default:
          cmp = a.health.diffCount - b.health.diffCount;
          break;
      }
      // Stable tie-break by name so equal-diff rows have a deterministic order.
      if (cmp === 0) return a.name.localeCompare(b.name);
      return cmp * dir;
    });
    return sorted;
  }, [rows, diffsOnly, hideGeneric, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Numeric columns default to descending (biggest first); name to ascending.
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const Th = ({
    label: thLabel,
    sort,
    align = 'left',
  }: {
    label: string;
    sort?: SortKey;
    align?: 'left' | 'right' | 'center';
  }) => {
    const alignClass =
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
    return (
      <th className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase ${alignClass}`}>
        {sort ? (
          <button
            type="button"
            onClick={() => toggleSort(sort)}
            className="uppercase hover:text-gray-700"
          >
            {thLabel}
            {sortIndicator(sort)}
          </button>
        ) : (
          thLabel
        )}
      </th>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Phase 2 audit across all {manufacturers.length} manufacturers. Compares the
        hand-curated <code className="font-mono">brandIdsManualInclude</code> against the
        ref-derived <code className="font-mono">brandIdsDerivedFromRefs</code> set. Hover a row to
        promote / exclude / demote a brand or set the primary inline — actions write immediately.
        Click a chip label to see its contributing refs; click the row to open the manufacturer.
      </p>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={diffsOnly}
            onChange={(e) => setDiffsOnly(e.target.checked)}
            className="rounded text-brand-600 focus:ring-brand-500"
          />
          <span className="text-gray-700">Has diffs only</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideGeneric}
            onChange={(e) => setHideGeneric(e.target.checked)}
            className="rounded text-brand-600 focus:ring-brand-500"
          />
          <span className="text-gray-700">Hide generic manufacturers</span>
        </label>
        <span className="text-xs text-gray-500">
          {withDiffsCount} of {rows.length} have manual/derived diffs
        </span>
      </div>

      <ChipLegend items={['new', 'stale', 'excluded', 'primary']} />
      <p className="text-[11px] text-gray-400 -mt-2">
        Diff chips below use these tones — hover a legend entry for the full meaning.
      </p>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <Th label="Manufacturer" sort="name" />
                  <Th label="Manual" sort="manual" align="right" />
                  <Th label="Derived" sort="derived" align="right" />
                  <Th label="Suggested generic" />
                  <Th label="Primary" />
                  <Th label="Diff" sort="diff" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map(({ manufacturer: m, name, health }) => (
                  <tr
                    key={m.id}
                    onClick={() => onOpenManufacturer(m)}
                    className="group cursor-pointer hover:bg-brand-50/50"
                  >
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-gray-900">{name}</div>
                      <div className="text-xs text-gray-400 font-mono">{m.id}</div>
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums text-gray-700">
                      {health.manual.length}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums text-gray-700">
                      {health.derived.length}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-1.5">
                        {classificationBadge(health)}
                        {health.isGenericDisagrees && (
                          <span
                            title={
                              health.currentIsGeneric === undefined
                                ? 'isGeneric not set'
                                : `stored: ${health.currentIsGeneric ? 'generic' : 'non-generic'}`
                            }
                            className="text-[10px] text-amber-600"
                          >
                            ≠ stored
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <PrimaryCell
                        manufacturer={m}
                        health={health}
                        brandsById={brandsById}
                        actions={actions}
                        onDrill={(brandId) => setDrill({ manufacturer: m, brandId })}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {health.diffCount === 0 && health.exclusions.length === 0 ? (
                        <Badge variant="success">clean</Badge>
                      ) : (
                        <DiffCell
                          manufacturer={m}
                          health={health}
                          brandsById={brandsById}
                          actions={actions}
                          onDrill={(brandId) => setDrill({ manufacturer: m, brandId })}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {visible.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">
              {diffsOnly || hideGeneric
                ? 'No manufacturers match the filters.'
                : 'No manufacturers.'}
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm?.title ?? ''}
        size="sm"
      >
        {confirm && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">{confirm.body}</p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirm.onConfirm}>
                {confirm.confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ManufacturerBrandDrillDown
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        manufacturerId={drill?.manufacturer.id ?? ''}
        manufacturerName={
          drill ? drill.manufacturer.displayName || drill.manufacturer.name || drill.manufacturer.id : ''
        }
        brandId={drill?.brandId ?? ''}
        brandsById={brandsById}
      />
    </div>
  );
}

/**
 * "Primary" cell surfacing the states a reviewer distinguishes at a glance:
 *   • no current → suggestion is proposing one ("none set" cue) + inline Set primary
 *   • current === suggested → all good (single chip + muted "matches")
 *   • current ≠ suggested → both shown, "Current ★X → Suggested ★Y" + inline Set primary
 *   • current set but suggestion suppressed (generic / very-broad) → current + muted note
 * The Set-primary button applies the suggested brand without opening the editor.
 */
function PrimaryCell({
  manufacturer,
  health,
  brandsById,
  actions,
  onDrill,
}: {
  manufacturer: ManufacturerCatalogueEntry;
  health: ManufacturerDataHealth;
  brandsById: ReturnType<typeof useWatchBrandsMap>['brandsById'];
  actions: RowActions;
  onDrill: (brandId: string) => void;
}) {
  const cur = health.currentPrimaryBrandId;
  const sug = health.suggestedPrimaryBrandId;

  if (!cur && !sug) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  // Current set, but no suggestion (suppressed for generic / very-broad makers).
  if (cur && !sug) {
    return (
      <div className="flex items-center gap-1.5">
        <BrandChip
          id={cur}
          brandsById={brandsById}
          tone="primary"
          prefix="★"
          onClick={() => onDrill(cur)}
        />
        <span className="text-[10px] text-gray-400" title="No primary suggested for this manufacturer.">
          no suggestion
        </span>
        <ChipButton
          tone="danger"
          disabled={actions.busy}
          onClick={() => actions.clearPrimary(manufacturer)}
          title="Clear the primary brand"
          className={REVEAL}
        >
          ✕ Clear
        </ChipButton>
      </div>
    );
  }

  // No current — the suggestion is proposing one to accept.
  if (!cur && sug) {
    return (
      <div className="flex items-center gap-1.5">
        <BrandChip
          id={sug}
          brandsById={brandsById}
          tone="primary"
          prefix="★"
          onClick={() => onDrill(sug)}
        />
        <span
          className="text-[10px] font-medium text-amber-600"
          title="No primary set — apply the suggestion."
        >
          none set
        </span>
        <ChipButton
          tone="positive"
          disabled={actions.busy}
          onClick={() => actions.setPrimary(manufacturer, sug)}
          title={`Set the suggested brand as primary`}
          className={REVEAL}
        >
          ★ Set primary
        </ChipButton>
      </div>
    );
  }

  // Both set. `primaryDisagrees` distinguishes match vs mismatch.
  if (!health.primaryDisagrees) {
    return (
      <div className="flex items-center gap-1.5">
        <BrandChip
          id={cur!}
          brandsById={brandsById}
          tone="primary"
          prefix="★"
          onClick={() => onDrill(cur!)}
        />
        <span className="text-[10px] text-gray-400" title="Current primary matches the suggestion.">
          matches
        </span>
        <ChipButton
          tone="danger"
          disabled={actions.busy}
          onClick={() => actions.clearPrimary(manufacturer)}
          title="Clear the primary brand"
          className={REVEAL}
        >
          ✕ Clear
        </ChipButton>
      </div>
    );
  }

  // Current set but differs from the suggestion — show both, current → suggested.
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      <span className="text-[10px] uppercase tracking-wide text-gray-400">Current</span>
      <BrandChip
        id={cur!}
        brandsById={brandsById}
        tone="primary"
        prefix="★"
        onClick={() => onDrill(cur!)}
      />
      <ChipButton
        tone="danger"
        disabled={actions.busy}
        onClick={() => actions.clearPrimary(manufacturer)}
        title="Clear the current primary brand"
        className={REVEAL}
      >
        ✕ Clear
      </ChipButton>
      <span className="text-amber-600" title="Current primary differs from the suggestion." aria-hidden>
        →
      </span>
      <span className="text-[10px] uppercase tracking-wide text-amber-600">Suggested</span>
      <BrandChip
        id={sug!}
        brandsById={brandsById}
        tone="primary"
        prefix="★"
        onClick={() => onDrill(sug!)}
      />
      <ChipButton
        tone="positive"
        disabled={actions.busy}
        onClick={() => actions.setPrimary(manufacturer, sug!)}
        title="Set the suggested brand as primary"
        className={REVEAL}
      >
        ★ Set primary
      </ChipButton>
    </div>
  );
}

function DiffCell({
  manufacturer,
  health,
  brandsById,
  actions,
  onDrill,
}: {
  manufacturer: ManufacturerCatalogueEntry;
  health: ManufacturerDataHealth;
  brandsById: ReturnType<typeof useWatchBrandsMap>['brandsById'];
  actions: RowActions;
  onDrill: (brandId: string) => void;
}) {
  // Show new-in-derived first (promote candidates), then stale-in-manual, then
  // excluded (restore candidates) — each group capped so a busy row stays compact.
  const shownNew = health.newInDerived.slice(0, CHIP_CAP);
  const capAfterNew = Math.max(0, CHIP_CAP - shownNew.length);
  const shownStale = health.staleInManual.slice(0, capAfterNew);
  const capAfterStale = Math.max(0, capAfterNew - shownStale.length);
  const shownExcluded = health.exclusions.slice(0, capAfterStale);
  const total = health.newInDerived.length + health.staleInManual.length + health.exclusions.length;
  const overflow = total - shownNew.length - shownStale.length - shownExcluded.length;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {shownNew.map((id) => (
        <span key={`n-${id}`} className="inline-flex items-center gap-1">
          <BrandChip
            id={id}
            brandsById={brandsById}
            tone="new"
            prefix="+"
            onClick={() => onDrill(id)}
          />
          <ChipButton
            tone="positive"
            disabled={actions.busy}
            onClick={() => actions.promote(manufacturer, id)}
            title={`Add ${brandLabel(brandsById, id)} to the manual list`}
            className={REVEAL}
          >
            + Promote
          </ChipButton>
          <ChipButton
            tone="danger"
            disabled={actions.busy}
            onClick={() => actions.exclude(manufacturer, id)}
            title={`Exclude ${brandLabel(brandsById, id)} from the effective set`}
            className={REVEAL}
          >
            ✕ Exclude
          </ChipButton>
        </span>
      ))}
      {shownStale.map((id) => (
        <span key={`s-${id}`} className="inline-flex items-center gap-1">
          <BrandChip
            id={id}
            brandsById={brandsById}
            tone="stale"
            prefix="−"
            onClick={() => onDrill(id)}
          />
          <ChipButton
            tone="danger"
            disabled={actions.busy}
            onClick={() => actions.confirmDemote(manufacturer, id)}
            title={`Remove ${brandLabel(brandsById, id)} from the manual list`}
            className={REVEAL}
          >
            Demote
          </ChipButton>
        </span>
      ))}
      {shownExcluded.map((id) => (
        <span key={`x-${id}`} className="inline-flex items-center gap-1">
          <BrandChip
            id={id}
            brandsById={brandsById}
            tone="excluded"
            onClick={() => onDrill(id)}
          />
          <ChipButton
            disabled={actions.busy}
            onClick={() => actions.unexclude(manufacturer, id)}
            title={`Restore ${brandLabel(brandsById, id)} to the effective set`}
            className={REVEAL}
          >
            ↺ Restore
          </ChipButton>
        </span>
      ))}
      {overflow > 0 && <span className="text-xs text-gray-400">+{overflow} more</span>}
    </div>
  );
}
